import type {ReactElement, ReactNode} from 'react';
import {useEffect, useState} from 'react';
import {Button, ButtonSize, ButtonType} from '@Components/button';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {AuthInput} from '@Components/auth-input';
import {INPUT_FIELD_TYPE} from '@Components/input-field';
import {LoginType} from '@Components/two-factor-authentication/two-factor-authentication.types';
import type {TOTPVerificationResponse} from '@Components/two-factor-authentication/two-factor-authentication.types';
import {submit2FACodeForVerification} from '@Components/two-factor-authentication/two-factor-authentication-thunk';
import {clearError, setRedirectUrl, updateLoginType} from '@Components/two-factor-authentication/two-factor-authentication-reducer';
import {
  useTwoFactorAuthenticationErrorMessage,
  useTwoFactorAuthenticationIsLoading,
  useTwoFactorAuthenticationPanelLoginType,
} from '@Components/two-factor-authentication/two-factor-authentication.hooks';
import {consumePendingTwoFactorVerificationCallback, isTOTPLoginType} from '@Libraries/two-factor-authentication-library';
import {getPostLoginRedirectUrl} from '@Libraries/login-signup-library';
import {POST_2FA_LOGIN_FALLBACK_REDIRECT_URI} from '@Panels/two-factor-authentication-verification-panel/two-factor-authentication-verification-panel.types';
import {useLoginPageRedirectUrl} from '@Components/login-page/login-page.hooks';
import {executeThunk} from '@Utils/thunk.util';
import {noop} from '@Utils/general.util';
import {VerificationCodeInput} from './components/verification-code-input/verification-code-input';
import {BACKUP_CODE_PLACEHOLDER} from './two-factor-verification-form.types';
import styles from './two-factor-verification-form.module.scss';
import {useAppDispatch} from '@/hooks';

/**
 * The login page's own 2FA step. It shares the verification slice and thunks with
 * `TwoFactorAuthenticationVerificationPanel` — the modal every other surface still uses — and differs only
 * in presentation: an inline panel with six code boxes rather than a dialog with one text field.
 */
export function TwoFactorVerificationForm(): ReactElement {
  const [code, setCode] = useState('');

  const dispatch = useAppDispatch();
  const loginType = useTwoFactorAuthenticationPanelLoginType();

  /** A code typed in one view must not carry into the other, whichever control switched them. */
  useEffect(() => {
    setCode('');
  }, [loginType]);
  const errorMessage = useTwoFactorAuthenticationErrorMessage();
  const isLoading = useTwoFactorAuthenticationIsLoading();
  const redirectUrl = useLoginPageRedirectUrl();

  const isTotp = (): boolean => {
    return isTOTPLoginType(loginType);
  };

  const getVerificationUrl = (): string => {
    return isTotp() ? 'authenticate/verifyTOTP' : 'authenticate/verifyUserTwoFactorAuthenticationBackupCode';
  };

  const onVerificationSuccess = (response?: TOTPVerificationResponse): void => {
    const pendingCallback = consumePendingTwoFactorVerificationCallback();
    if (pendingCallback) {
      pendingCallback();
      return;
    }

    window.location.href = getPostLoginRedirectUrl(POST_2FA_LOGIN_FALLBACK_REDIRECT_URI, response?.preferredLanguage);
  };

  const verifyCode = async (submittedCode: string): Promise<void> => {
    dispatch(setRedirectUrl(redirectUrl));
    await executeThunk(
      () => {
        return dispatch(submit2FACodeForVerification({code: submittedCode, url: getVerificationUrl()}));
      },
      onVerificationSuccess,
      noop
    );
  };

  const onPrimaryCtaClick = (): void => {
    void verifyCode(code);
  };

  const handleCodeChange = (updatedCode: string): void => {
    setCode(updatedCode);
  };

  const handleBackupCodeChange = (txt: string): void => {
    setCode(txt.trim());
  };

  /** Swaps between the authenticator code and a backup code, which is also how the backup view cancels. */
  const onSecondaryCtaClick = (): void => {
    dispatch(updateLoginType(isTotp() ? LoginType.BACKUP_CODE : LoginType.TOTP));
    dispatch(clearError());
    setCode('');
  };

  const getHintRow = (hintKey: string): ReactNode => {
    if (errorMessage !== '') {
      return (
        <div className={`${styles.hintRow} ${styles.hintRowError}`}>
          <Icon icon="icon-exclamation-triangle" size={IconSize.SIZE_ICON_16} />
          <Text dangerouslySetInnerHTML size={TextSize.SM} val={errorMessage} />
        </div>
      );
    }

    return (
      <div className={styles.hintRow}>
        <Icon icon="icon-info" size={IconSize.SIZE_ICON_16} />
        <Text size={TextSize.SM} val={window.i18next.t(hintKey)} />
      </div>
    );
  };

  const getCodeEntry = (): ReactNode => {
    if (!isTotp()) {
      return (
        <div className={styles.backupCodeEntry}>
          <AuthInput
            enableFocusStyles
            errorText={errorMessage}
            errorTextContainsHtml
            handleInput={handleBackupCodeChange}
            label={window.i18next.t('pmwjs_two_factor_authentication_backup_code')}
            onInputSubmit={onPrimaryCtaClick}
            placeholder={BACKUP_CODE_PLACEHOLDER}
            showError={errorMessage !== ''}
            type={INPUT_FIELD_TYPE.TEXT}
          />
          {getBackupCodeHint()}
        </div>
      );
    }

    return (
      <div className={styles.codeEntry}>
        <VerificationCodeInput hasError={errorMessage !== ''} onChange={handleCodeChange} onSubmit={onPrimaryCtaClick} value={code} />
        {getHintRow('pmwjs_two_factor_authentication_code_hint')}
      </div>
    );
  };

  /** The field already carries the error state, so the CTA stays pressable — as it does in the modal. */
  const getBackupCodeHint = (): ReactNode => {
    if (errorMessage !== '') {
      return null;
    }
    return getHintRow('pmwjs_two_factor_authentication_backup_code_hint');
  };

  /** The backup view's hint belongs to the field, so it sits closer to the CTAs than the code boxes do. */
  const getFormLayoutClasses = (): string => {
    return isTotp() ? styles.formLayout : `${styles.formLayout} ${styles.backupCodeLayout}`;
  };

  return (
    <div className={getFormLayoutClasses()}>
      {getCodeEntry()}

      <div className={styles.ctaRow}>
        <Button
          customClasses={styles.ctaButton}
          onClick={onSecondaryCtaClick}
          size={ButtonSize.MEDIUM}
          text={isTotp() ? window.i18next.t('pmwjs_two_factor_authentication_dont_have_app') : window.i18next.t('pmwjs_cancel')}
          type={ButtonType.SECONDARY}
        />
        <Button
          customClasses={styles.ctaButton}
          isLoading={isLoading}
          onClick={onPrimaryCtaClick}
          size={ButtonSize.MEDIUM}
          text={isTotp() ? window.i18next.t('pmwjs_log_in') : window.i18next.t('pmwjs_submit')}
          type={ButtonType.PRIMARY}
        />
      </div>
    </div>
  );
}
